'use client';

import FeaturePageHeader from '~/components/FeaturePageHeader';
import FeaturePageShell, { featurePageContentClassName } from '~/components/FeaturePageShell';
import Skeleton from '~/components/Loading/Skeleton';
import { cn } from '~/lib/utils';

function ChartCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'border-border/80 bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
        className,
      )}
    >
      <div className="space-y-1 px-5 pt-4 pb-2">
        <Skeleton className="h-4 w-28" />
      </div>
      <div className="px-5 pb-4">
        <Skeleton className="h-[200px] w-full rounded-lg" />
      </div>
    </div>
  );
}

function WorkTableSkeleton() {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3 w-14" />
      </div>
      <div className="border-border bg-card overflow-hidden rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="space-y-0 px-3 py-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3 py-3.5">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Overview / Home first-paint + pending-route skeleton. */
export default function HomeDashboardSkeleton() {
  return (
    <FeaturePageShell aria-busy="true" aria-label="Loading Overview">
      <FeaturePageHeader
        title={<Skeleton className="h-5 w-16" />}
        actions={<Skeleton className="h-8 w-32" />}
      />

      <div className={cn(featurePageContentClassName, 'space-y-8 py-6')}>
        <div className="space-y-3 lg:space-y-4">
          <ChartCardSkeleton />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
            <ChartCardSkeleton />
            <ChartCardSkeleton />
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-muted flex h-11 w-fit items-center gap-1 rounded-xl p-1 sm:h-12">
            <Skeleton className="h-9 w-40 rounded-md sm:h-10" />
            <Skeleton className="h-9 w-44 rounded-md sm:h-10" />
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4 [&>*]:min-w-0">
            <WorkTableSkeleton />
            <WorkTableSkeleton />
          </div>
        </div>
      </div>
    </FeaturePageShell>
  );
}
